import type { ReactNode } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, CircleAlertIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface PersistentListCardEmptyProps {
  icon: ReactNode
  title: string
  description: string
}

export interface PersistentListCardProps<T> {
  /** True while a fetch is in flight. */
  loading: boolean
  /**
   * True once a response (success OR error) has been received at least once.
   * Drives the spinner-vs-content switch — without this we'd flash the
   * spinner on every refresh including errors.
   */
  initialLoaded: boolean
  /** Full-width error card. Pass `null` to suppress. */
  error?: string | null
  /** Title inside the error card. Defaults to "Couldn't load". */
  errorTitle?: string
  /** Click handler for the "Try again" button inside the error card. */
  onRetry?: () => void
  /**
   * Empty-state content. Pass an object to render the dashed card; pass
   * `null` to skip the empty card entirely (parent will render its own).
   */
  empty?: PersistentListCardEmptyProps | null
  /** Items to render with `renderItem`. */
  items?: T[]
  /** Stable key per item. */
  itemKey: (item: T) => string | number
  /**
   * Renders each row. Parent owns per-row state — the card is a pure
   * presentation surface so callbacks like optimistic-remove stay close to
   * the data hooks that own them.
   */
  renderItem: (item: T) => ReactNode
  /**
   * Optional caption rendered above the row list (e.g.
   * "Showing X of Y on this page.").
   */
  infoText?: ReactNode
  /** Current page (1-indexed). */
  page?: number
  /** Items per page. */
  pageSize?: number
  /** Total items on the server (across all pages). */
  total?: number
  /** Click handler for previous / next. */
  onPageChange?: (page: number) => void
  /** While true, prev/next are disabled. */
  pageLoading?: boolean
}

/**
 * Body of a list screen. Drives the four UI states (initial-load spinner,
 * full-width error card with retry, dashed empty card, row list) and
 * optionally renders a pagination footer. Future list screens should not
 * need to re-implement the chrome around their rows.
 */
export function PersistentListCard<T>({
  loading,
  initialLoaded,
  error,
  errorTitle = "Couldn't load",
  onRetry,
  empty,
  items,
  itemKey,
  renderItem,
  infoText,
  page,
  pageSize,
  total,
  onPageChange,
  pageLoading,
}: PersistentListCardProps<T>) {
  const hasPagination =
    page !== undefined &&
    pageSize !== undefined &&
    total !== undefined &&
    onPageChange !== undefined
  const isInitialLoad = loading && !initialLoaded
  const safeItems = items ?? []
  const totalPages = hasPagination
    ? Math.max(1, Math.ceil((total as number) / (pageSize as number)))
    : 1
  const showPagination =
    hasPagination && !isInitialLoad && totalPages > 1

  return (
    <div className="flex flex-col gap-3">
      {/* Error state — full-width recoverable */}
      {!isInitialLoad && error && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
          <CircleAlertIcon className="mt-0.5 size-5 shrink-0 text-destructive" />
          <div className="flex flex-1 flex-col gap-2">
            <p className="text-sm font-medium">{errorTitle}</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            {onRetry && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={onRetry}
              >
                Try again
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Initial-load spinner */}
      {isInitialLoad && (
        <div className="flex items-center justify-center py-12" aria-hidden="true">
          <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground border-t-primary" />
        </div>
      )}

      {/* Empty state */}
      {!isInitialLoad && !error && safeItems.length === 0 && empty && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border py-16 text-center">
          <div className="flex size-10 items-center justify-center rounded-lg text-muted-foreground">
            {empty.icon}
          </div>
          <p className="text-sm font-medium">{empty.title}</p>
          <p className="text-xs text-muted-foreground">{empty.description}</p>
        </div>
      )}

      {/* Row list */}
      {!isInitialLoad && !error && safeItems.length > 0 && (
        <div className="flex flex-col gap-3">
          {infoText}
          {safeItems.map((item) => (
            <div key={itemKey(item)}>{renderItem(item)}</div>
          ))}
        </div>
      )}

      {/* Pagination footer */}
      {showPagination && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                onPageChange?.(Math.max(1, (page as number) - 1))
              }
              disabled={(page as number) <= 1 || pageLoading}
            >
              <ChevronLeftIcon />
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                onPageChange?.(Math.min(totalPages, (page as number) + 1))
              }
              disabled={(page as number) >= totalPages || pageLoading}
            >
              Next
              <ChevronRightIcon />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
